import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useGetUser } from '@/api';
import { useWithdrawBonuses } from '@/api/bonuses.ts';
import { Button } from '@/components/shared/Button';
import { Input } from '@/components/shared/Input/Input.tsx';
import { WithdrawalSchema } from '@/components/shared/Modal/content/withdrawal/constants.ts';
import { WithdrawalStatus } from '@/components/shared/Modal/content/withdrawal/WithdrawalStatus/WithdrawalStatus.tsx';
import { formatNumberToFullStr } from '@/utils';

import cn from './styles.module.sass';

interface WithdrawalData {
    phone: string;
    sum: string;
}

export const WithdrawalContent = () => {
    const { data: userData } = useGetUser();
    const { mutateAsync: withdrawBonuses } = useWithdrawBonuses();

    const { control, handleSubmit, trigger, formState, setError, clearErrors, setValue } =
        useForm<WithdrawalData>({
            defaultValues: {
                phone: userData ? formatNumberToFullStr(userData?.msisdn) : '',
                sum: '',
            },
            mode: 'all',
            resolver: zodResolver(WithdrawalSchema),
        });

    const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

    const handleChange = (value: WithdrawalData['sum']) => {
        if (userData && parseInt(value) > userData.balance) {
            setError('root', { message: 'Недостаточно бонусов на балансе' });
        } else {
            clearErrors('root');
        }
    };

    const onSubmit = async (data: WithdrawalData) => {
        const { errors, status: resStatus } =
            (await withdrawBonuses({
                sum: Number(data.sum),
            })) || {};

        const limitExceededError = errors?.find(err => err.type === 'limit_exceeded');

        if (resStatus && resStatus >= 400) {
            if (limitExceededError) {
                setError('root', { message: 'Превышен лимит' });
            } else {
                setStatus('error');
            }

            return;
        }

        setStatus('success');
    };

    useEffect(() => {
        if (userData) {
            setValue('phone', formatNumberToFullStr(userData?.msisdn));
        }
    }, [userData]);

    return (
        <div className={cn.container}>
            {status === 'idle' ? (
                <>
                    <div className={cn.head}>
                        <h2>Вывод бонусов</h2>
                        <p>
                            Вывод бонусов возможен только на логин кабинета (номер телефона).
                            Максимальная сумма для вывода в месяц - 2000 бонусов
                        </p>
                    </div>

                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className={cn.inputContainer}>
                            <Controller
                                render={({ field: { value, onChange, onBlur } }) => (
                                    <Input
                                        containerClassName={cn.phoneInputContainer}
                                        value={value}
                                        onChange={onChange}
                                        onBlur={onBlur}
                                        label="Номер телефона (логин Другомании)"
                                        hasClearBtn={false}
                                        disabled
                                    />
                                )}
                                name="phone"
                                control={control}
                            />
                            <Controller
                                render={({
                                    field: { value, onChange, onBlur },
                                    fieldState: { error },
                                    formState: { errors },
                                }) => (
                                    <Input
                                        containerClassName={cn.pointsInputContainer}
                                        type="tel"
                                        mask="9999"
                                        maskChar=""
                                        error={
                                            (!!error?.message && error.type === 'custom') ||
                                            !!errors.root?.message
                                        }
                                        helperText={
                                            (error?.type === 'custom' ? error?.message : '') ||
                                            errors.root?.message
                                        }
                                        value={value}
                                        onChange={e => {
                                            handleChange(e.target.value);
                                            onChange(e);
                                        }}
                                        onBlur={onBlur}
                                        label="Количество бонусов"
                                        onClear={e => {
                                            e.preventDefault();
                                            clearErrors('sum');
                                            setValue('sum', '');
                                            trigger('sum');
                                        }}
                                    />
                                )}
                                name="sum"
                                control={control}
                            />
                        </div>

                        <Button disabled={!formState.isValid || !!formState.errors.root}>
                            Вывести бонусы
                        </Button>
                    </form>
                </>
            ) : (
                <WithdrawalStatus isSuccess={status === 'success'} />
            )}
        </div>
    );
};
